// src/features/auth/components/PasswordTooltip.tsx
import React, { useState, useRef, useEffect } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import PasswordRequirements from './PasswordRequirements';

interface PasswordTooltipProps {
  password: string;
  email?: string;
  className?: string;
}

const PasswordTooltip: React.FC<PasswordTooltipProps> = ({ 
  password, 
  email = '', 
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Calculate tooltip position based on viewport
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If not enough space below (less than 400px), show above
      setPosition(spaceBelow < 400 && spaceAbove > 200 ? 'top' : 'bottom');
    }
  }, [isOpen]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current && 
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Tooltip trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => {
          // Delay closing to allow moving to tooltip
          setTimeout(() => {
            if (!tooltipRef.current?.matches(':hover')) {
              setIsOpen(false);
            }
          }, 100);
        }}
        className="inline-flex items-center text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark rounded-full p-1"
        aria-label="Password requirements"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <InformationCircleIcon className="w-4 h-4" />
      </button>

      {/* Tooltip content */}
      {isOpen && (
        <div
          ref={tooltipRef}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          className={`absolute z-50 w-80 p-4 bg-theme-tooltip-bg dark:bg-dark-theme-tooltip-bg border border-theme-tooltip-border dark:border-dark-theme-tooltip-border rounded-lg shadow-lg ${
            position === 'top' 
              ? 'bottom-full mb-2' 
              : 'top-full mt-2'
          } left-1/2 transform -translate-x-1/2`}
          style={{
            boxShadow: `0 10px 15px -3px var(--theme-tooltip-shadow, rgba(0, 0, 0, 0.1)), 0 4px 6px -2px var(--theme-tooltip-shadow, rgba(0, 0, 0, 0.05))`
          }}
          role="tooltip"
          aria-live="polite"
        >
          {/* Arrow */}
          <div 
            className={`absolute left-1/2 transform -translate-x-1/2 w-3 h-3 bg-theme-tooltip-bg dark:bg-dark-theme-tooltip-bg border-theme-tooltip-border dark:border-dark-theme-tooltip-border rotate-45 ${
              position === 'top'
                ? 'top-full border-r border-b -mt-1.5'
                : 'bottom-full border-l border-t -mb-1.5'
            }`}
          />
          
          {/* Content */}
          <div className="relative">
            <PasswordRequirements 
              password={password} 
              email={email}
              showTitle={true}
              className="text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordTooltip;
